// src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

// global utility to get variant key
export const getVariantKey = (product) =>
    `${product.itemCode}_${String(product.colorId ?? '')}_${String(product.sizeId ?? '')}_${String(product.userId ?? '')}`;

export const CartProvider = ({ children }) => {
    // 1️⃣ load initial from localStorage (or empty)
    const [cartItems, setCartItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('cart')) || {};
        } catch {
            return {};
        }
    });

    const [itemOrder, setItemOrder] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('cartOrder')) || [];
        } catch {
            return [];
        }
    });

    // 2️⃣ persist to localStorage on every change
    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
        localStorage.setItem('cartOrder', JSON.stringify(itemOrder));
    }, [cartItems, itemOrder]);

    // Add or update item
    const addItem = (product, qty) => {
        const variantKey = getVariantKey(product);

        setCartItems((prev) => {
            const existingItem = prev[variantKey];
            const updatedQty = existingItem ? existingItem.qty + qty : qty;

            return {
                ...prev,
                [variantKey]: {
                    product,
                    qty: updatedQty,
                },
            };
        });

        // 🟢 Add to itemOrder if it's new
        setItemOrder((prev) => {
            const order = Array.isArray(prev) ? prev : [];
            if (!order.includes(variantKey)) {
                return [...order, variantKey];
            }
            return order;
        });
    };

    // Update quantity
    const updateQty = (variantKey, qty) => {
        setCartItems((prev) => {
            if (qty < 1) {
                const { [variantKey]: _, ...rest } = prev;
                return rest;
            }
            return {
                ...prev,
                [variantKey]: {
                    ...prev[variantKey],
                    qty,
                },
            };
        });
    };

    // Update color and preserve card order
    const updateColor = (variantKey, colorId) => {
        let newKey = '';

        setCartItems((prev) => {
            const item = prev[variantKey];
            if (!item) return prev;

            const updatedProduct = { ...item.product, colorId };
            newKey = getVariantKey(updatedProduct);

            const { [variantKey]: _, ...rest } = prev;

            return {
                ...rest,
                [newKey]: {
                    product: updatedProduct,
                    qty: item.qty,
                },
            };
        });

        setItemOrder((prev) => {
            const order = Array.isArray(prev) ? prev : [];
            const index = order.indexOf(variantKey);
            if (index === -1 || !newKey) return order;

            const newOrder = [...order];
            newOrder.splice(index, 1, newKey);
            return newOrder;
        });
    };

    // Update size and preserve card order
    const updateSize = (variantKey, sizeId) => {
        let newKey = '';

        setCartItems((prev) => {
            const item = prev[variantKey];
            if (!item) return prev;

            const updatedProduct = { ...item.product, sizeId };
            newKey = getVariantKey(updatedProduct);

            const { [variantKey]: _, ...rest } = prev;

            return {
                ...rest,
                [newKey]: {
                    product: updatedProduct,
                    qty: item.qty,
                },
            };
        });

        setItemOrder((prev) => {
            const order = Array.isArray(prev) ? prev : [];
            const index = order.indexOf(variantKey);
            if (index === -1 || !newKey) return order;

            const newOrder = [...order];
            newOrder.splice(index, 1, newKey);
            return newOrder;
        });
    };

    // Remove item
    const removeItem = (variantKey) => {
        setCartItems((prev) => {
            const { [variantKey]: _, ...rest } = prev;
            return rest;
        });

        setItemOrder((prev) => {
            const order = Array.isArray(prev) ? prev : [];
            return order.filter((key) => key !== variantKey);
        });
    };

    // Clear all
    const clearCart = () => {
        setCartItems({});
        setItemOrder([]);
    };

    // compute grand total
    const total = Object.values(cartItems).reduce(
        (sum, { product, qty }) => sum + product.saleRate * qty,
        0
    );

    return (
        <CartContext.Provider
            value={{
                cartItems,
                itemOrder,
                addItem,
                updateQty,
                updateColor,
                updateSize,
                removeItem,
                clearCart,
                total,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);